import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { SELECTED_BROKER_ID } from '../../constants/auth';
import { useAuth } from '../../hooks/useAuth/useAuth';
import { UserContext } from '../../hooks/useUser/useUser';
import type { IUserInfoResult } from '../../interfaces/user';
import Users from '../../services/api/users/users';
import { getFromLocalStorage, removeFromLocalStorage, toLocalStorage } from '../../services/storage/storage';

const UserContextProvider = ({ children }: PropsWithChildren) => {
  const [userInfo, setUserInfo] = useState<IUserInfoResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(() =>
    getFromLocalStorage(SELECTED_BROKER_ID)
  );
  const { isLoggedIn } = useAuth();

  const persistSelectedBrokerId = useCallback((brokerId: string | null) => {
    setSelectedBrokerId(brokerId);
    if (brokerId) {
      toLocalStorage(SELECTED_BROKER_ID, brokerId);
    } else {
      removeFromLocalStorage(SELECTED_BROKER_ID);
    }
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(false);
      const data = await Users.getUserInfo();
      setUserInfo(data);
    } catch {
      setUserInfo((prev) => prev);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn === null) return;
    if (isLoggedIn) {
      fetchUserInfo();
    } else {
      setUserInfo(null);
      setFetchError(false);
      setLoading(false);
      persistSelectedBrokerId(null);
    }
  }, [isLoggedIn, fetchUserInfo, persistSelectedBrokerId]);

  useEffect(() => {
    if (!userInfo) return;
    if (selectedBrokerId && !userInfo.brokers.some((broker) => broker.brokerId === selectedBrokerId)) {
      persistSelectedBrokerId(null);
    }
  }, [userInfo, selectedBrokerId, persistSelectedBrokerId]);

  const value = useMemo(
    () => ({
      userInfo,
      loading,
      fetchError,
      selectedBrokerId,
      fetchUserInfo,
      setSelectedBrokerId: persistSelectedBrokerId
    }),
    [userInfo, loading, fetchError, selectedBrokerId, fetchUserInfo, persistSelectedBrokerId]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContextProvider;
